import {
  IconSelector,
  IconPencil,
  IconTrash,
  IconMailForward,
} from "@tabler/icons-react";
import { getInitials, colourForName, formatDateFull } from "./data";
import styles from "../../pages/ClientDashboard/TeamManagement/teamManagement.module.css";
import Button from "../UI/Button/button";
import PendingBadge from "./PendingBadge";
import RoleBadge from "./RoleBadge";

const COLUMNS = ["Name", "Team", "Role", "Last Login"];

function LastLoginCell({ iso }) {
  const formatted = formatDateFull(iso);
  if (!formatted)
    return <span className={styles.neverLoggedIn}>Never logged in</span>;
  return (
    <div className={styles.dateCell}>
      <div>{formatted.dateStr}</div>
      <div className={styles.dateCellTime}>{formatted.timeStr}</div>
    </div>
  );
}

export default function UsersTable({
  users,
  currentUserId,
  canActOn,
  onEditUser,
  onRemoveUser,
  onResendInvite,
}) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th key={col}>
                <div className={styles.thInner}>
                  {col.toUpperCase()} <IconSelector size={14} />
                </div>
              </th>
            ))}
            <th style={{ textAlign: "right" }}>
              <div
                className={styles.thInner}
                style={{ justifyContent: "flex-end" }}
              >
                ACTIONS
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const colour = colourForName(u.name);
            const isSelf = u._id === currentUserId;
            const manageable = canActOn(u);
            const canEdit = isSelf || manageable;
            const canRemove = manageable && !isSelf && !u.isOwner;
            const canResend = manageable && u.status === "pending";

            return (
              <tr key={u._id}>
                <td>
                  <div className={styles.userCell}>
                    <div
                      className={styles.userAvatar}
                      style={{ background: colour }}
                    >
                      {getInitials(u?.name)}
                    </div>
                    <div>
                      <div className={styles.userName}>{u?.name}</div>
                      <div className={styles.userEmail}>{u?.email}</div>
                      {u?.status === "pending" && (
                        <div style={{ marginTop: 3 }}>
                          <PendingBadge />
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  {u?.teamId ? (
                    <span
                      className={styles.teamChip}
                      style={
                        u?.teamId?.colour
                          ? {
                              background: `${u?.teamId?.colour}1a`,
                              color: u?.teamId?.colour,
                            }
                          : undefined
                      }
                    >
                      {u?.teamId?.name}
                    </span>
                  ) : (
                    <span className={`${styles.teamChip} ${styles.teamChipNa}`}>
                      —
                    </span>
                  )}
                </td>
                <td>
                  <RoleBadge role={u?.role} />
                </td>
                <td>
                  <LastLoginCell iso={u?.lastLogin} />
                </td>
                <td>
                  <div className={styles.rowActions}>
                    {canResend && (
                      <Button
                        variant="icon"
                        size="sm"
                        className={styles.rowIconBtn}
                        aria-label="Resend invite"
                        title="Resend invite"
                        onClick={() => onResendInvite(u?._id)}
                      >
                        <IconMailForward size={15} />
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="icon"
                        size="sm"
                        className={styles.rowIconBtn}
                        aria-label="Edit user"
                        onClick={() => onEditUser(u?._id)}
                      >
                        <IconPencil size={15} />
                      </Button>
                    )}
                    {canRemove && (
                      <Button
                        variant="icon"
                        size="sm"
                        className={`${styles.rowIconBtn} ${styles.rowIconDanger}`}
                        aria-label="Remove user"
                        onClick={() => onRemoveUser(u?._id)}
                      >
                        <IconTrash size={15} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
